import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { getAllAppCatalogue, getAppServices } from './appCatalogue.service';
import AppCatalogueColumn from './appCatalogue.column';
import Loader from '../../../components/loader';
import GenericDataGrid from '../../../components/genericTable';
import { createManagementTableSx } from '../../../styles/tableStyles';
import {
  managementCreateButtonSx,
  managementHeaderActionSx,
  managementHeaderContentSx,
  managementHeaderSx,
  managementPageSubtitleSx,
  managementPageTitleSx,
} from '../../../styles/managementStyles';
import AlertToast from '../../../components/alertToast';
import { buttonContentSx } from '../../../styles/dialogStyles';
import InlineAlert from '../../../components/inlineAlert';

const dataGridSx = createManagementTableSx();

export default function AppCatalogue() {
  const navigate = useNavigate();
  const [appCatalogueData, setAppCatalogueData] = useState([]);
  const [errors, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [alert, setAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const getAppCatalogueData = useCallback(
    async (page = currentPage, limit = rowsPerPage) => {
      setLoading(true);
      const res = await getAllAppCatalogue(page, limit);
      setLoading(false);

      if (Array.isArray(res.data)) {
        const counts = await Promise.all(
          res.data.map((app) =>
            getAppServices(app.id).then((r) => (Array.isArray(r?.data) ? r.data.length : 0)).catch(() => 0)
          )
        );
        const dataWithCounts = res.data.map((app, i) => ({ ...app, serviceCount: counts[i] }));
        setAppCatalogueData(dataWithCounts);
        setTotalDataCount(res.data.length);
        setError('');
      } else {
        setAppCatalogueData([]);
        setError('An error occurred while fetching data. Please try again later.');
      }
    },
    [currentPage, rowsPerPage]
  );

  useEffect(() => {
    getAppCatalogueData(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, getAppCatalogueData]);

  const appCatalogueCoulmnData = AppCatalogueColumn({
    limit: rowsPerPage,
    page: currentPage,
    navigate,
    getAppCatalogueData,
    setAlert,
    setOpenAlert,
    setResponseMsg,
    setLoading,
  });

  return (
    <>
      <Box sx={managementHeaderSx}>
        <Box sx={managementHeaderContentSx}>
          <Typography className="app-page-title" sx={managementPageTitleSx}>
            App Catalogue Management
          </Typography>
          <Typography sx={managementPageSubtitleSx}>
            Manage applications in the catalogue including creation, updates, and availability status.
          </Typography>
        </Box>
        <Box sx={managementHeaderActionSx}>
          <Button
            variant="contained"
            onClick={() => {
              setLoading(true);
              navigate('/addAppCatalogue');
            }}
            sx={{
              ...managementCreateButtonSx,
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: '100%', md: 260 },
            }}
          >
            <Box component="span" sx={buttonContentSx}>
              <Icon icon="tabler:plus" width="18" height="18" />
              <span>CREATE APP</span>
            </Box>
          </Button>
        </Box>
      </Box>

      <InlineAlert
        message={openAlert && alert === 'false' ? responseMsg : ''}
        onClose={alertClose}
        variant="error"
      />

      <Box sx={{ width: '100%' }}>
        <GenericDataGrid
          rows={appCatalogueData}
          columns={appCatalogueCoulmnData}
          rowCount={totalDataCount}
          page={currentPage}
          pageSize={rowsPerPage}
          loading={loading}
          errors={errors}
          checkbox="false"
          dataGridSx={dataGridSx}
          rowHeight={60}
          onPaginationChange={(page, pageSize) => {
            setCurrentPage(page);
            setRowsPerPage(pageSize);
            getAppCatalogueData(page, pageSize);
          }}
        />
      </Box>

      <AlertToast
        open={openAlert && alert === 'true'}
        onClose={alertClose}
        message={responseMsg}
        variant="success"
      />

      {loading && <Loader />}
    </>
  );
}
